import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { CustomDialog } from '../../../components/StyledComponents';
import { DialogTitle, DialogContent, DialogActions, FormControl, Tooltip } from '@mui/material';
import { ControlpanelErrorButton, ControlpanelSecondaryButton, ControlpanelSuccessButton, GrayIconButton } from '../../../components/StyledButton';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect from '../../../components/CustomSelect';
import CustomInputHorizontal from '../../../components/CustomInputHorizontal';
import LeaveApi from '../../../axios/LeaveAndHoliday/LeaveApi';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { format, parse, isValid, startOfDay } from 'date-fns';
import CorfirmApproveRequestDialog from './CorfirmApproveRequestDialog';
import CorfirmRejectRequestDialog from './CorfirmRejectRequestDialog';
import CheckIcon from '@mui/icons-material/Check';
import CustomDateTimePicker_01 from '../../../components/CustomDatePickerTime_01';
import { getLeaveByUser, getLeaveByUserByLeaveId, getUserList } from '../../../axios/User/UserApi';
import WarningIcon from '@mui/icons-material/Warning';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarV2Icon16pxSvg from '../../../assets/images/icons/CalendarNoBGV2Icon16pxSvg';
import DefaultTooltip, { LeaveUsedDaysTooltipContent } from '../../../components/DefaultTooltip';
import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg';
dayjs.extend(utc);
dayjs.extend(timezone);

/** Hiển thị số thập phân tối đa 2 chữ số, số nguyên giữ nguyên */
const formatDecimalNumber = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return value ?? 0;
    if (Number.isInteger(numberValue)) return numberValue;
    return Number(numberValue.toFixed(2));
};

const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

const parseTimeStringToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const parts = String(timeStr).split(":").map(Number);
    const [h = 0, m = 0, s = 0] = parts;
    return h * 60 + m + Math.floor(s / 60);
};

const normalizeHoursPerLeaveDay = (value) => {
    if (!value) return "08:00:00";
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed || "08:00:00";
    }
    return "08:00:00";
};

const minutesToHhMmSs = (totalMinutes) => {
    const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

/** Làm tròn 2 chữ số thập phân */
const formatOneDecimal = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return 0;
    const rounded = Math.round(numberValue * 100) / 100;
    return Number.isInteger(rounded) ? rounded : rounded;
};

/**
 * usedFromYear (HH:mm:ss hoặc số ngày legacy) / hoursPerLeaveDay → số ngày
 */
const parseUsedFromYearToDays = (usedFromYear, hoursPerLeaveDay) => {
    if (usedFromYear == null || usedFromYear === "") return 0;

    const asString = String(usedFromYear).trim();
    if (asString.includes(":")) {
        const timeMins = parseTimeStringToMinutes(asString);
        const dayMins =
            parseTimeStringToMinutes(hoursPerLeaveDay) || DEFAULT_FULL_DAY_MINUTES;
        if (dayMins <= 0) return 0;
        return timeMins / dayMins;
    }

    const asNumber = Number(usedFromYear);
    return Number.isFinite(asNumber) ? asNumber : 0;
};

const formatLeaveBalanceDisplay = (value, hoursPerLeaveDay) =>
    formatDecimalNumber(parseUsedFromYearToDays(value, hoursPerLeaveDay));

const resolveItemHoursPerLeaveDay = (item) =>
    normalizeHoursPerLeaveDay(
        item?.hoursPerLeaveDaySnapshot || item?.hoursPerLeaveDay
    );

const toLeaveBalanceItems = (leaveBalance) => {
    if (!leaveBalance) return [];
    return Array.isArray(leaveBalance) ? leaveBalance.filter(Boolean) : [leaveBalance];
};

const sumUsedFromYearDays = (items) =>
    toLeaveBalanceItems(items).reduce(
        (sum, item) =>
            sum +
            parseUsedFromYearToDays(item.usedFromYear, resolveItemHoursPerLeaveDay(item)),
        0
    );

const buildUsedFromYearDetails = (items) =>
    toLeaveBalanceItems(items).map((item) => {
        const hoursPerDay = resolveItemHoursPerLeaveDay(item);
        const usedRaw = item.usedFromYear;
        const usedTime =
            typeof usedRaw === "string" && String(usedRaw).includes(":")
                ? normalizeHoursPerLeaveDay(usedRaw)
                : minutesToHhMmSs(
                      parseUsedFromYearToDays(usedRaw, hoursPerDay) *
                          (parseTimeStringToMinutes(hoursPerDay) || DEFAULT_FULL_DAY_MINUTES)
                  );
        return {
            usedTime,
            hoursPerDay,
            days: formatOneDecimal(parseUsedFromYearToDays(usedRaw, hoursPerDay)),
        };
    });

/** Ưu tiên numOfTime; fallback numOfDay (dữ liệu cũ) → số ngày hiển thị */
const resolveDisplayDays = (data) => {
    if (!data) return null;
    if (data.numOfTime) {
        const timeMins = parseTimeStringToMinutes(data.numOfTime);
        const dayMins =
            parseTimeStringToMinutes(data.hoursPerLeaveDaySnapshot) ||
            DEFAULT_FULL_DAY_MINUTES;
        if (dayMins <= 0) return 0;
        return formatDecimalNumber(timeMins / dayMins);
    }
    if (data.numOfDay != null && data.numOfDay !== "") {
        return formatDecimalNumber(data.numOfDay);
    }
    return null;
};

const resolveApproveReason = (responseReason, requestType, fallbackReason = "") => {
    const normalizedType = String(requestType || "").toUpperCase();
    const isEditOrDelete =
        normalizedType === "EDIT_LEAVE" || normalizedType === "DELETE_LEAVE";

    if (responseReason?.trim()) {
        return responseReason;
    }

    if (isEditOrDelete) {
        return fallbackReason || "";
    }

    return responseReason || fallbackReason || "";
};

const DetailRequestDialog = ({ onClose, leaveInfo, onRefresh, leaveTypesData, policiesList, isRequestMode = false }) => {
    const { t } = useTranslation()
    const { dateTimeFormat, isAdmin, defaultReason, genaralSettingDateFormat } = useAuth();
    const { getLeaveById, getLeaveRequestId, getLeaveRequestIdHistory } = LeaveApi()
    const [openCorfirmApproveRequest, setOpenCorfirmApproveRequest] = useState(false);
    const [openCorfirmRejectRequest, setOpenCorfirmRejectRequest] = useState(false);
    const [leaveByUser, setLeaveByUserData] = useState([]);
    const [activeTab, setActiveTab] = useState("details");
    const [requestDetail, setRequestDetail] = useState(null);
    const [requestHistory, setRequestHistory] = useState([]);
    const [allUser, setAllUser] = useState([]);
    const [loadingCount, setLoadingCount] = useState(0);
    const MAX_REASON_LENGTH = 255;
    const isLoading = loadingCount > 0;

    const toDateForPicker = (value, format) => {
        if (!value) return null;

        // 1. Đã là Date
        if (value instanceof Date && isValid(value)) {
            return value;
        }

        if (typeof value === "string") {
            const clean = value.replace(/\u00A0/g, " ").trim();

            // 2. ISO string → new Date()
            if (/^\d{4}-\d{2}-\d{2}T/.test(clean)) {
                const isoDate = new Date(clean);
                return isValid(isoDate) ? isoDate : null;
            }

            // 3. Đúng format setting → parse theo setting
            const parsed = parse(clean, format, new Date());
            if (isValid(parsed)) return parsed;
        }

        return null;
    };

    const normalizeDateFnsFormat = (fmt) => {
        return fmt
            // YEAR
            .replace(/YYYY/g, "yyyy")
            .replace(/YY/g, "yy")

            // DAY
            .replace(/DD/g, "dd")

            // MINUTE (fix sm ❌)
            .replace(/sm/g, "mm")
            .replace(/MM/g, "MM");
    };

    const safeDateTimeFormat = normalizeDateFnsFormat(dateTimeFormat);

    // Hàm parse chung cho mọi date từ API
    const parseApiDateTime = (value) => {
        if (!value) return null;

        const clean = String(value).replace(/\u00A0/g, " ").trim();

        let d = parse(clean, "yyyy-MM-dd HH:mm:ss", new Date());
        if (!isNaN(d)) return d;

        d = parse(clean, "dd/MM/yyyy HH:mm:ss", new Date());
        if (!isNaN(d)) return d;

        // ISO 8601
        d = new Date(clean);
        if (!isNaN(d.getTime())) return d;

        return null;
    };

    const parseNewData = (newData) => {
        if (!newData) return {};
        if (typeof newData === "string") {
            try {
                return JSON.parse(newData) || {};
            } catch {
                return {};
            }
        }
        return newData;
    };

    const formatRequestDateTime = (value) => {
        if (!value) return "-";
        const parsedDate = value instanceof Date ? value : parseApiDateTime(value);
        if (!parsedDate || !isValid(parsedDate)) return "-";
        return format(parsedDate, safeDateTimeFormat);
    };

    const getRequestStatusLabel = (statusId) => {
        if (Number(statusId) === 1) return t("common.pending-request");
        if (Number(statusId) === 2) return t("common.approved");
        if (Number(statusId) === 3) return t("common.rejected");
        return "-";
    };

    const getRequestStatusColor = (statusId) => {
        if (Number(statusId) === 1) return "#F97316";
        if (Number(statusId) === 2) return "#16A34A";
        if (Number(statusId) === 3) return "#DC2626";
        return "#525252";
    };

    const getLeaveTypeLabel = (leaveTypeId) => {
        const leaveType = leaveTypesData?.find(
            (item) => String(item.leaveTypeId) === String(leaveTypeId)
        );
        if (!leaveType) return "-";

        const langId = Number(localStorage.getItem("i18nextLngId"));
        if (langId === 2) return leaveType.remarkEN || leaveType.remark || leaveType.leaveTypeName || "-";
        return leaveType.remark || leaveType.remarkEN || leaveType.leaveTypeName || "-";
    };

    const leavePolicyData = [
        { value: 1, label: t('common.normal') },
        { value: 2, label: t('common.restricted') },
    ];

    const initialRequestType = String(leaveInfo?.requestType || "").toUpperCase();
    const initialRequestStatusId = leaveInfo?.requestStatusId ?? leaveInfo?.approvalStatusId;
    const initialResponseReason =
        initialRequestType === "EDIT_LEAVE" || initialRequestType === "DELETE_LEAVE"
            ? (Number(initialRequestStatusId) !== 1 ? leaveInfo?.responseReason : "")
            : leaveInfo?.responseReason;

    const [formData, setFormData] = useState({
        userId: leaveInfo?.userId,
        selectUser: leaveInfo?.name,
        requestedFrom: toDateForPicker(
            leaveInfo.requestStartDate,
            safeDateTimeFormat
        ),
        requestedTo: toDateForPicker(
            leaveInfo.requestEndDate,
            safeDateTimeFormat
        ),

        approvedFrom: toDateForPicker(
            leaveInfo.requestStartDate,
            safeDateTimeFormat
        ),
        approvedTo: toDateForPicker(
            leaveInfo.requestEndDate,
            safeDateTimeFormat
        ),
        leaveType: leaveInfo?.leaveTypeId,
        leavePolicy: leaveInfo?.leavePolicyId,
        requestReason: leaveInfo?.requestReason,
        approveReason: resolveApproveReason(
            initialResponseReason,
            leaveInfo?.requestType,
            defaultReason
        ),
        selecStatusLeave: leaveInfo?.approvalStatusId,
        createdTime: leaveInfo?.createdTime ? toDateForPicker(leaveInfo.createdTime, safeDateTimeFormat) : null
    });

    useEffect(() => {
        if (!isRequestMode) return;
        if (!leaveInfo?.leaveRequestId && !leaveInfo?.leaveId) return;

        const fetchLeaveRequestDetail = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const response = await getLeaveRequestId({
                    leaveRequestId: leaveInfo.leaveRequestId ?? leaveInfo.leaveId,
                    langId: localStorage.getItem("i18nextLngId"),
                });

                if (
                    response?.data?.message !== "Successful" &&
                    response?.data?.message !== "Thành công"
                ) {
                    return;
                }

                const detail = response?.data?.data?.[0];
                if (!detail) return;

                const newData = parseNewData(detail.newData);
                const oldData = parseNewData(detail.oldData);
                const requestType = String(detail.requestType || "").toUpperCase();
                const displayData = requestType === "DELETE_LEAVE" ? oldData : newData;
                setRequestDetail({ ...detail, newData, oldData });
                setFormData((prev) => ({
                    ...prev,
                    userId: detail.userId ?? displayData.userId,
                    selectUser: detail.fullName || prev.selectUser,
                    requestedFrom: parseApiDateTime(displayData.start),
                    requestedTo: parseApiDateTime(displayData.end),
                    approvedFrom: parseApiDateTime(displayData.start),
                    approvedTo: parseApiDateTime(displayData.end),
                    leaveType: displayData.leaveTypeId,
                    leavePolicy: displayData.leavePolicyId,
                    requestReason: detail.requestReason ?? displayData.requestReason ?? "",
                    approveReason: resolveApproveReason(
                        detail.responseReason,
                        requestType,
                        defaultReason
                    ),
                    selecStatusLeave: detail.requestStatusId,
                    createdTime: detail.createdTime || detail.createdAt || detail.createAt || null,
                    displayDays: resolveDisplayDays(displayData),
                    modifiedBy: detail.modifiedBy,
                }));
            } catch (error) {
                toast.error(error?.response?.data?.message || error.message);
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };

        fetchLeaveRequestDetail();
    }, [isRequestMode, leaveInfo?.leaveRequestId, leaveInfo?.leaveId, defaultReason]);

    useEffect(() => {
        if (!defaultReason || !isRequestMode) return;

        const requestType = String(
            requestDetail?.requestType || leaveInfo?.requestType || ""
        ).toUpperCase();

        if (requestType !== "EDIT_LEAVE" && requestType !== "DELETE_LEAVE") return;

        setFormData((prev) => {
            if (prev.approveReason?.trim()) return prev;
            return { ...prev, approveReason: defaultReason };
        });
    }, [
        defaultReason,
        isRequestMode,
        requestDetail?.requestType,
        leaveInfo?.requestType,
    ]);

    useEffect(() => {
        // không có leaveId thì thôi
        if (isRequestMode || !leaveInfo?.leaveId) return;

        const fetchLeaveDetail = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const res = await getLeaveById({
                    leaveId: leaveInfo.leaveId,
                });

                const data = res?.data?.data?.[0];

                if (!data) return;

                setFormData({
                    userId: data.userId,
                    selectUser: data.name,
                    requestedFrom: parseApiDateTime(data.requestStartDate),
                    requestedTo: parseApiDateTime(data.requestEndDate),
                    approvedFrom: data.requestStartDate,
                    approvedTo: data.requestEndDate,
                    leaveType: data.leaveTypeId,
                    // nếu API có trường leavePolicy thì dùng, không thì fallback
                    leavePolicy: data.leavePolicy ?? leavePolicyData[0].value,
                    requestReason: data.requestReason,
                    approveReason: data.responseReason || defaultReason || "",
                    selecStatusLeave: data.approvalStatusId,
                    createdTime: parseApiDateTime(data.createdTime)
                });
            } catch (error) {
                console.error('getLeaveById error: ', error);
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };

        fetchLeaveDetail();
    }, [leaveInfo?.leaveId]);

    const [leaveByUserByLeave, setLeaveByUserByLeaveData] = useState([]);

    const fetchLeaveByUserData = async (userId) => {
        if (!userId) return;

        const payload = {
            langId: localStorage.getItem("i18nextLngId"),
            userId: userId,
            year: new Date().getFullYear()
        };

        try {
            setLoadingCount((prev) => prev + 1);
            const response = await getLeaveByUser(payload);
            if (
                response?.data?.message === "Successful" ||
                response?.data?.message === "Thành công"
            ) {
                setLeaveByUserData(response.data.data ?? []);
            }
        } catch (error) {
            if (error?.response && error.response.status !== 401) {
                toast.error(error?.response?.data?.message || "Error");
            } else {
                toast.error(t('common.network-timeout'));
            }
        } finally {
            setLoadingCount((prev) => Math.max(prev - 1, 0));
        }
    };

    const fetchLeaveByUserByLeaveId = async (userId, leaveTypeId) => {
        if (!userId || !leaveTypeId) return;

        const payload = {
            langId: localStorage.getItem("i18nextLngId"),
            userId: userId,
            leaveTypeId: leaveTypeId,
            year: new Date().getFullYear()
        };

        try {
            setLoadingCount((prev) => prev + 1);
            const response = await getLeaveByUserByLeaveId(payload);
            if (
                response?.data?.message === "Successful" ||
                response?.data?.message === "Thành công"
            ) {
                setLeaveByUserByLeaveData(response.data.data ?? null);
            }
        } catch (error) {
            if (error?.response && error.response.status !== 401) {
                toast.error(error?.response?.data?.message || "Error");
            } else {
                toast.error(t('common.network-timeout'));
            }
        } finally {
            setLoadingCount((prev) => Math.max(prev - 1, 0));
        }
    };

    useEffect(() => {
        if (!formData.userId || !formData.leaveType) return;

        fetchLeaveByUserByLeaveId(
            formData.userId,
            formData.leaveType
        );
        fetchLeaveByUserData(formData.userId);
    }, [formData.userId, formData.leaveType]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const response = await getUserList({
                    statusId: 1,
                    page: 1,
                    pageSize: 1000000,
                    status: null,
                    langId: localStorage.getItem("i18nextLngId"),
                });
                setAllUser(response?.data?.data || []);
            } catch (error) {
                console.error(error);
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };

        fetchUsers();
    }, []);

    useEffect(() => {
        if (!isRequestMode) return;

        const leaveRequestId = leaveInfo?.leaveRequestId ?? leaveInfo?.leaveId;
        if (!leaveRequestId) return;

        const fetchHistory = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const response = await getLeaveRequestIdHistory({
                    leaveRequestId,
                    langId: localStorage.getItem("i18nextLngId"),
                });
                const historyData = response?.data?.data;
                setRequestHistory(Array.isArray(historyData) ? historyData : []);
            } catch (error) {
                console.error("getLeaveRequestIdHistory error:", error);
                setRequestHistory([]);
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };

        fetchHistory();
    }, [isRequestMode, leaveInfo?.leaveRequestId, leaveInfo?.leaveId]);

    const getFullNameByEmail = useCallback((email) => {
        if (!email) return "-";
        const normalizedEmail = String(email).trim().toLowerCase();
        const user = allUser.find(
            (u) => u.email && u.email.trim().toLowerCase() === normalizedEmail
        );
        return user?.fullName || email;
    }, [allUser]);

    const getLeaveHistoryActionLabel = useCallback((requestStatusName) => {
        const status = String(requestStatusName || "").toUpperCase();
        if (status === "APPROVED") return t("common.leave-history-action-approved");
        if (status === "REJECTED") return t("common.leave-history-action-rejected");
        return t("common.leave-history-action-pending");
    }, [t]);

    const getLeaveHistoryTypeLabel = useCallback((requestType) => {
        const type = String(requestType || "").toUpperCase();
        if (type === "EDIT_LEAVE") return t("common.leave-history-type-edit");
        if (type === "DELETE_LEAVE") return t("common.leave-history-type-delete");
        return t("common.leave-history-type-add");
    }, [t]);

    const parsedLeaveHistory = useMemo(() => {
        return (requestHistory || []).map((item) => {
            const newData = parseNewData(item?.newData);
            const oldData = parseNewData(item?.oldData);
            const requestType = String(item?.requestTypeName || "").toUpperCase();
            const displayData = requestType === "DELETE_LEAVE" ? oldData : newData;
            const requestStatusName = String(
                item?.requestStatusName || item?.requestStatus || ""
            ).toUpperCase();
            const actorEmail = item?.modifiedBy || item?.modifyBy || item?.createdBy;
            const actorName =
                item?.fullName ||
                getFullNameByEmail(actorEmail);

            return {
                ...item,
                requestType,
                requestStatusName,
                actorName,
                createdAt: item?.createdAt || item?.createdTime || null,
                timeRangeLabel: `${formatRequestDateTime(displayData.start)} - ${formatRequestDateTime(displayData.end)}`,
                leaveTypeLabel: getLeaveTypeLabel(displayData.leaveTypeId ?? item?.leaveTypeId),
                reasonLabel:
                    requestStatusName === "PENDING"
                        ? (item?.requestReason || displayData?.requestReason || "-")
                        : (item?.responseReason || displayData?.responseReason || item?.requestReason || "-"),
                actionLabel: getLeaveHistoryActionLabel(requestStatusName),
                typeLabel: getLeaveHistoryTypeLabel(requestType),
            };
        });
    }, [
        requestHistory,
        getFullNameByEmail,
        getLeaveHistoryActionLabel,
        getLeaveHistoryTypeLabel,
        leaveTypesData,
        safeDateTimeFormat,
    ]);

    const resolveHoursPerLeaveDaySnapshot = useCallback(
        (leaveTypeId) => {
            const fromTypes = (leaveTypesData || []).find(
                (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
            );
            const fromBalance = (leaveByUserByLeave || []).find(
                (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
            ) || leaveByUserByLeave?.[0];
            return normalizeHoursPerLeaveDay(
                fromTypes?.hoursPerLeaveDay ??
                    fromBalance?.hoursPerLeaveDay ??
                    fromBalance?.hoursPerLeaveDaySnapshot ??
                    "08:00:00"
            );
        },
        [leaveTypesData, leaveByUserByLeave]
    );

    /** SUM(usedFromYear / snapshot) khi cùng leaveTypeId; hoursPerDay nằm trong từng phần tử details */
    const leaveEntitlementInfo = useMemo(() => {
        if (!leaveByUserByLeave?.length) {
            return {
                text: "—",
                usedDays: null,
                details: [],
            };
        }

        const details = buildUsedFromYearDetails(leaveByUserByLeave);
        const usedDays = formatOneDecimal(sumUsedFromYearDays(leaveByUserByLeave));
        const totalDays = formatDecimalNumber(
            leaveByUserByLeave[0].totalEntitlementYear
        );

        return {
            text: `${usedDays}/${totalDays}`,
            usedDays,
            details,
        };
    }, [leaveByUserByLeave]);

    const carryLeaveText = useMemo(() => {
        if (!leaveByUserByLeave?.length) return null;

        const item = leaveByUserByLeave[0];
        if (!item.isCarry) return null;

        if (item.expiredDt) {
            const now = new Date();
            const expiredDate = new Date(item.expiredDt);

            // ✅ bỏ giờ
            now.setHours(0, 0, 0, 0);
            expiredDate.setHours(0, 0, 0, 0);

            if (now > expiredDate) {
                return null;
            }
        }

        const expired = item.expiredDt
            ? format(new Date(item.expiredDt), genaralSettingDateFormat)
            : "";
        const hoursPerDay = resolveItemHoursPerLeaveDay(item);

        return `${formatLeaveBalanceDisplay(item.usedFromCarry, hoursPerDay)}/${formatLeaveBalanceDisplay(item.carryIn, hoursPerDay)} (${t("common.expire-on")} ${expired})`;
    }, [leaveByUserByLeave, t, genaralSettingDateFormat]);



    const handleCloseDialog = () => {
        onClose();
    };

    const handleOpenApproveRequest = () => {
        setOpenCorfirmApproveRequest(true)
    }

    const handleCloseApproveRequest = () => {
        setOpenCorfirmApproveRequest(false)
    }

    const handleOpenRejectRequest = () => {
        setOpenCorfirmRejectRequest(true)
    }

    const handleCloseRejectRequest = () => {
        setOpenCorfirmRejectRequest(false)
    }

    const [validationMessages, setValidationMessages] = useState({
        selectUser: "",
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "approveReason" && value.length > MAX_REASON_LENGTH) {
            return;
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const [checkRequestedTo, setCheckRequestedTo] = useState(false)
    const [checkRequestedFrom, setCheckRequestedFrom] = useState(false)

    useEffect(() => {
        setCheckRequestedTo(formData.requestedTo?.target?.value ? formData.requestedTo.target.value.getTime() : formData.requestedTo?.getTime());
        setCheckRequestedFrom(formData.requestedFrom?.target?.value ? formData.requestedFrom.target.value.getTime() : formData.requestedFrom?.getTime());
    }, [formData.requestedFrom, formData.requestedTo]);

    const toDate = (v) => {
        if (!v) return new Date(NaN);
        if (dayjs.isDayjs(v)) return v.toDate();
        if (v instanceof Date) return v;
        return new Date(v); // fallback cho string
    };

    const requestedDays = useMemo(() => {
        if (!formData.requestedFrom || !formData.requestedTo) return 0;

        const start = startOfDay(toDate(formData.requestedFrom));
        const end = startOfDay(toDate(formData.requestedTo));

        const diff =
            Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

        return diff > 0 ? diff : 0;
    }, [formData.requestedFrom, formData.requestedTo]);

    const isLeaveByHour = Number(formData.leaveType) === 1;

    const isOverEntitlement = useMemo(() => {
        if (formData.leaveType === 1) return false; // leave theo giờ bỏ qua

        if (!leaveByUserByLeave?.length) return false;

        const item = leaveByUserByLeave[0];

        const totalEntitlementYear = Number(item.totalEntitlementYear) || 0;
        const usedFromYear = sumUsedFromYearDays(leaveByUserByLeave);

        const isCarry = item.isCarry;
        const carryIn = Number(item.carryIn) || 0;
        const usedFromCarry = Number(item.usedFromCarry) || 0;

        let carryAvailable = 0;

        // ✅ check carry còn hạn
        if (isCarry && item.expiredDt) {
            const now = new Date();
            const expiredDate = new Date(item.expiredDt);

            if (now <= expiredDate) {
                carryAvailable = Math.max(carryIn - usedFromCarry, 0);
            }
        }

        const totalAvailable =
            (totalEntitlementYear - usedFromYear) + carryAvailable;

        return requestedDays > totalAvailable;
    }, [leaveByUserByLeave, requestedDays, formData.leaveType, resolveHoursPerLeaveDaySnapshot]);

    if (isRequestMode) {
        const requestStatusId = requestDetail?.requestStatusId ?? formData.selecStatusLeave;
        const requestType = String(requestDetail?.requestType || leaveInfo?.requestType || "").toUpperCase();
        const isDeleteLeaveRequest = requestType === "DELETE_LEAVE";
        const isEditLeaveRequest = requestType === "EDIT_LEAVE";
        const requestTitle = requestType === "EDIT_LEAVE"
            ? t("common.edit-request-leave")
            : isDeleteLeaveRequest
                ? t("common.request-delete-leave")
                : t("common.leave-request-title");
        const hasApproveReason = Boolean(formData.approveReason?.trim());
        const inputClassName = "w-full h-10 rounded-lg border border-[#E5E7EB] bg-[#e5e7eb] px-3 text-sm text-[#111827] outline-none";
        const textAreaClassName = "w-full rounded-lg border border-[#E5E7EB] bg-[#e5e7eb] px-3 py-2 text-sm text-[#111827] outline-none resize-none";

        const oldLeaveData = requestDetail?.oldData || {};
        const newLeaveData = requestDetail?.newData || {};
        const oldLeaveTypeId = oldLeaveData.leaveTypeId;
        const newLeaveTypeId = formData.leaveType ?? newLeaveData.leaveTypeId;
        const oldDateRangeText = `${formatRequestDateTime(oldLeaveData.start)}  →  ${formatRequestDateTime(oldLeaveData.end)}`;
        const newDateRangeText = `${formatRequestDateTime(formData.requestedFrom)}  →  ${formatRequestDateTime(formData.requestedTo)}`;
        const oldNumOfDay = resolveDisplayDays(oldLeaveData) ?? "";
        const newNumOfDay =
            formData.displayDays ??
            resolveDisplayDays(newLeaveData) ??
            "";
        const comparisonRowClassName =
            "grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] gap-3 items-end";
        const comparisonArrow = (
            <div className="hidden md:flex justify-center text-2xl text-[#525252] pb-1.5">→</div>
        );

        return (
            <CustomDialog maxWidth={'md'} open loading={isLoading} onClose={handleCloseDialog} keepMounted>
                <DialogTitle>
                    <div className='flex justify-between'>
                        <div className='flex items-center gap-4'>
                            <p className='text-xl font-semibold'>{requestTitle}</p>
                        </div>
                        <GrayIconButton onClick={handleCloseDialog} className='self-start' disabled={isLoading}>
                            <CloseIcon />
                        </GrayIconButton>
                    </div>
                </DialogTitle>

                <DialogContent>
                    <div className="grid grid-cols-2 border-b border-[#E5E7EB] mb-5">
                        <button
                            type="button"
                            onClick={() => setActiveTab("details")}
                            className={`py-3 text-sm font-semibold border-b-2 ${
                                activeTab === "details"
                                    ? "text-[#0057FF] border-[#0057FF]"
                                    : "text-[#6B7280] border-transparent"
                            }`}
                        >
                            {t("button.detail")}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("history")}
                            className={`py-3 text-sm font-semibold border-b-2 ${
                                activeTab === "history"
                                    ? "text-[#0057FF] border-[#0057FF]"
                                    : "text-[#6B7280] border-transparent"
                            }`}
                        >
                            {t("common.history")}
                        </button>
                    </div>

                    {activeTab === "details" ? (
                        isDeleteLeaveRequest ? (
                            <div className="space-y-3">
                                <div className="rounded-md bg-[#F3F4F6] border-l-[6px] border-[#0057FF] p-4">
                                    <div className="flex items-center gap-3 flex-wrap text-sm mb-1">
                                        <span className="font-semibold text-[#111827]">{formData.selectUser || "-"}</span>
                                        <span className="text-[#6B7280]">{t('common.leave-request-title')}</span>
                                        <span
                                            className="font-medium"
                                            style={{ color: getRequestStatusColor(requestStatusId) }}
                                        >
                                            {getRequestStatusLabel(requestStatusId)}
                                        </span>
                                    </div>
                                    <p className="text-sm font-semibold text-[#111827] mb-1">
                                        {getLeaveTypeLabel(formData.leaveType)}
                                    </p>
                                    <div className="flex items-center gap-5 text-sm text-[#1F2937] flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <CalendarV2Icon16pxSvg />
                                            <span>
                                                {formatRequestDateTime(formData.requestedFrom)}
                                                {" - "}
                                                {formatRequestDateTime(formData.requestedTo)}
                                            </span>
                                        </div>
                                        {formData.displayDays ? (
                                            <div className="flex items-center gap-2">
                                                <AccessTimeIcon fontSize="small" className='text-[#6B7280]' />
                                                <span>{`${formData.displayDays} ${t('common.days')}`}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                    {/* <p className="text-sm text-[#6B7280] mt-1">
                                        {t('common.approve-reject-by')} {formData.modifiedBy || "-"}
                                    </p> */}
                                </div>

                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.reason")}
                                    </label>
                                    <textarea
                                        className={textAreaClassName}
                                        rows={3}
                                        value={formData.requestReason || ""}
                                        readOnly
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.approve-reject-reason")} <span className="text-[#DC2626]">*</span>
                                    </label>
                                    <textarea
                                        className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] outline-none resize-none"
                                        rows={3}
                                        name="approveReason"
                                        placeholder={t("common.enter-reason")}
                                        value={formData.approveReason || ""}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        ) : (
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-[#111827] mb-2">{t("common.status")}</p>
                                <p
                                    className="text-sm font-medium"
                                    style={{ color: getRequestStatusColor(requestStatusId) }}
                                >
                                    {getRequestStatusLabel(requestStatusId)}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.user")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={formData.selectUser || ""}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.request-time")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={formData.createdTime ? formatRequestDateTime(formData.createdTime) : "-"}
                                        readOnly
                                    />
                                </div>
                            </div>

                            {isEditLeaveRequest ? (
                                <div className={comparisonRowClassName}>
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.leave-type-old")}
                                        </label>
                                        <FormControl className="w-full">
                                            <CustomSelect
                                                name="oldLeaveType"
                                                value={oldLeaveTypeId}
                                                options={leaveTypesData}
                                                dataType="leaveType"
                                                readOnly
                                            />
                                        </FormControl>
                                    </div>
                                    {comparisonArrow}
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.leave-type-new")}
                                        </label>
                                        <FormControl className="w-full">
                                            <CustomSelect
                                                name="leaveType"
                                                value={newLeaveTypeId}
                                                options={leaveTypesData}
                                                dataType="leaveType"
                                                readOnly
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                            ) : null}

                            <div className={`grid grid-cols-1 ${isLeaveByHour || isEditLeaveRequest ? "" : "md:grid-cols-2"} gap-3`}>
                                {!isEditLeaveRequest && (
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.leave-type")}
                                        </label>
                                        <FormControl className="w-full">
                                            <CustomSelect
                                                name="leaveType"
                                                value={formData.leaveType}
                                                options={leaveTypesData}
                                                dataType="leaveType"
                                                readOnly
                                            />
                                        </FormControl>
                                    </div>
                                )}
                                {!isLeaveByHour && (
                                    <div>
                                        <div className="flex items-center gap-1 mb-2">
                                            <label className="block text-sm text-[#111827]">
                                                {t("common.leave-entitlement")}
                                            </label>
                                            {leaveEntitlementInfo.details?.length ? (
                                                <DefaultTooltip
                                                    title={t("common.leave-entitlement")}
                                                    readMore={false}
                                                    content={
                                                        <LeaveUsedDaysTooltipContent
                                                            details={leaveEntitlementInfo.details}
                                                            totalDays={leaveEntitlementInfo.usedDays}
                                                        />
                                                    }
                                                >
                                                    <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5 cursor-help" />
                                                </DefaultTooltip>
                                            ) : null}
                                        </div>
                                        <input
                                            className={inputClassName}
                                            value={leaveEntitlementInfo.text}
                                            readOnly
                                        />
                                    </div>
                                )}
                            </div>

                            {isEditLeaveRequest ? (
                                <>
                                    <div className={comparisonRowClassName}>
                                        <div>
                                            <label className="block text-sm text-[#111827] mb-2">
                                                {t("common.date-time-old")}
                                            </label>
                                            <input
                                                className={inputClassName}
                                                value={oldDateRangeText}
                                                readOnly
                                            />
                                        </div>
                                        {comparisonArrow}
                                        <div>
                                            <label className="block text-sm text-[#111827] mb-2">
                                                {t("common.date-time-new")}
                                            </label>
                                            <input
                                                className={inputClassName}
                                                value={newDateRangeText}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className={comparisonRowClassName}>
                                        <div>
                                            <label className="block text-sm text-[#111827] mb-2">
                                                {t("common.no-of-days-old")}
                                            </label>
                                            <input
                                                className={inputClassName}
                                                value={oldNumOfDay}
                                                readOnly
                                            />
                                        </div>
                                        {comparisonArrow}
                                        <div>
                                            <label className="block text-sm text-[#111827] mb-2">
                                                {t("common.no-of-days-new")}
                                            </label>
                                            <input
                                                className={inputClassName}
                                                value={newNumOfDay}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.date-time")}
                                        </label>
                                        <input
                                            className={inputClassName}
                                            value={`${formatRequestDateTime(formData.requestedFrom)}  →  ${formatRequestDateTime(formData.requestedTo)}`}
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.no-of-days")}
                                        </label>
                                        <input
                                            className={inputClassName}
                                            value={formData.displayDays ?? resolveDisplayDays(requestDetail?.newData) ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm text-[#111827] mb-2">
                                    {t("common.reason")}
                                </label>
                                <textarea
                                    className={textAreaClassName}
                                    rows={3}
                                    value={formData.requestReason || ""}
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-[#111827] mb-2">
                                    {t("common.approve-reject-reason")} <span className="text-[#DC2626]">*</span>
                                </label>
                                <textarea
                                    className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] outline-none resize-none"
                                    rows={3}
                                    name="approveReason"
                                    placeholder={t("common.enter-reason")}
                                    value={formData.approveReason || ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        )
                    ) : (
                        <div className="mt-4 relative">
                            {!parsedLeaveHistory.length ? (
                                <div className="py-10 text-center text-sm text-[#6B7280]">
                                    {t("common.no-data")}
                                </div>
                            ) : (
                                parsedLeaveHistory.map((item, index) => (
                                    <div key={item.id ?? index} className="flex gap-4 relative pb-8">
                                        {index !== parsedLeaveHistory.length - 1 && (
                                            <div className="absolute left-[19px] top-10 w-[2px] h-full bg-gray-200" />
                                        )}

                                        <div className="flex flex-col items-center z-10">
                                            <div
                                                className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                                                    item.requestStatusName === "APPROVED"
                                                        ? "bg-[#F0FDF4] border-[#E5E7EB]"
                                                        : item.requestStatusName === "REJECTED"
                                                            ? "bg-[#FEF2F2] border-[#E5E7EB]"
                                                            : "bg-[#FEF2E6] border-[#E5E7EB]"
                                                }`}
                                            >
                                                <div
                                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                        item.requestStatusName === "APPROVED"
                                                            ? "bg-green-500"
                                                            : item.requestStatusName === "REJECTED"
                                                                ? "bg-red-500"
                                                                : "bg-orange-400"
                                                    }`}
                                                >
                                                    {item.requestStatusName === "APPROVED" && (
                                                        <DoneIcon sx={{ color: "#fff", fontSize: 16 }} />
                                                    )}
                                                    {item.requestStatusName === "REJECTED" && (
                                                        <ClearIcon sx={{ color: "#fff", fontSize: 16 }} />
                                                    )}
                                                    {(item.requestStatusName === "PENDING" ||
                                                        !["APPROVED", "REJECTED"].includes(item.requestStatusName)) && (
                                                        <AccessTimeIcon sx={{ color: "#fff", fontSize: 16 }} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex-1 bg-white">
                                            <div className="flex items-start justify-between gap-3">
                                                <p className="font-semibold text-gray-900">
                                                    {item.actorName}{" "}
                                                    <span className="font-normal text-[#1F2937]">
                                                        {item.actionLabel} {item.typeLabel}
                                                    </span>
                                                </p>
                                                <p className="text-sm text-[#6B7280] whitespace-nowrap">
                                                    {formatRequestDateTime(item.createdAt)}
                                                </p>
                                            </div>

                                            <p className="text-sm text-gray-600 mt-1">
                                                <span className="text-gray-500">{t("common.content-036")}:</span>{" "}
                                                <span className="text-[#1F2937]">{item.timeRangeLabel}</span>
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                <span className="text-gray-500">{t("common.type")}:</span>{" "}
                                                <span className="text-[#1F2937]">{item.leaveTypeLabel}</span>
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                <span className="text-gray-500">{t("common.reason")}:</span>{" "}
                                                <span className="text-[#1F2937]">{item.reasonLabel}</span>
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </DialogContent>

                <DialogActions sx={{ borderTop: "1px solid #E5E7EB" }}>
                    <div className='flex gap-2 px-4 py-4'>
                        <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isLoading}>
                            <p>{t('button.cancel')}</p>
                        </ControlpanelSecondaryButton>
                        {activeTab === "details" && Number(requestStatusId) === 1 && (
                            <>
                                <ControlpanelErrorButton
                                    onClick={handleOpenRejectRequest}
                                    disabled={isLoading}
                                    sx={{
                                        opacity: hasApproveReason && !isLoading ? 1 : 0.5,
                                        pointerEvents: hasApproveReason && !isLoading ? "auto" : "none",
                                    }}
                                >
                                    <p>{t('button.reject')}</p>
                                </ControlpanelErrorButton>
                                <ControlpanelSuccessButton
                                    onClick={handleOpenApproveRequest}
                                    disabled={isLoading}
                                    sx={{
                                        opacity: hasApproveReason && !isLoading ? 1 : 0.5,
                                        pointerEvents: hasApproveReason && !isLoading ? "auto" : "none",
                                    }}
                                >
                                    <p>{t('button.approve')}</p>
                                </ControlpanelSuccessButton>
                            </>
                        )}
                    </div>
                </DialogActions>

                {openCorfirmApproveRequest && <CorfirmApproveRequestDialog onClose={handleCloseApproveRequest} formData={formData} onCloseDetail={handleCloseDialog} leaveInfo={leaveInfo} onRefresh={onRefresh} onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))} />}
                {openCorfirmRejectRequest && <CorfirmRejectRequestDialog onClose={handleCloseRejectRequest} formData={formData} onCloseDetail={handleCloseDialog} leaveInfo={leaveInfo} onRefresh={onRefresh} onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))} />}
            </CustomDialog>
        );
    }

    return (
        <CustomDialog maxWidth={'xl'} open loading={isLoading} onClose={handleCloseDialog} keepMounted>
            <DialogTitle>
                <div className='flex justify-between'>
                    <div className='flex items-center gap-4'>
                        <p className='text-xl font-semibold '>{t('common.detail-request-leave')}</p>
                    </div>
                    <GrayIconButton onClick={handleCloseDialog} className='self-start' disabled={isLoading}>
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                {/* <p className='pt-5 text-sm text-[#DC2626]'>{t('common.required-fields-are-marked-in-red')} "*".</p> */}
                <div className='grid grid-cols-6 items-center mt-3'>
                    <div className='col-span-6'>
                        <CustomInputHorizontal
                            required={false}
                            title={t('common.leave-staff')}
                            inputType="text"
                            name="selectUser"
                            disabled={true}
                            placeholder={t('common.leave-staff')}
                            handleInputChange={handleInputChange}
                            value={formData.selectUser}
                        />
                    </div>
                </div>
                <div className='pt-4 grid grid-cols-12 md:grid-cols-6 items-center'>
                    <div className='col-span-12 md:col-span-2'>
                        <p>{t('common.leave-time')}</p>
                    </div>
                    <div className='col-span-12 md:col-span-4'>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-x-2'>
                            <div className='col-span-12 md:col-span-1 mt-2 md:mt-0'>
                                <label className="block text-sm">
                                    {t('common.from')}
                                </label>
                                <div className='mt-2 w-full'>
                                    <CustomDateTimePicker_01
                                        className="w-full"
                                        value={
                                            formData.requestedFrom
                                        }
                                        onChange={(newValue) =>
                                            handleInputChange({ target: { name: "requestedFrom", value: newValue } })
                                        }
                                        readOnly
                                        formatDate={safeDateTimeFormat}
                                        disabled
                                    />

                                </div>
                            </div>
                            <div className='col-span-12 md:col-span-1 mt-2 md:mt-0'>
                                <label className="block text-sm">
                                    {t('common.to')}
                                </label>
                                <div className='mt-2 w-full'>
                                    <CustomDateTimePicker_01
                                        className="w-full"
                                        value={
                                            formData.requestedTo
                                        }
                                        onChange={(newValue) => handleInputChange({
                                            target: {
                                                name: "requestedTo",
                                                value: newValue
                                            }
                                        })}
                                        readOnly={true}
                                        // minDate={formData.requestedFrom}
                                        formatDate={safeDateTimeFormat}
                                        minDate={formData.requestedFrom?.target?.value || formData.requestedFrom}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {leaveInfo.approvalStatus === 'APPROVED' && (
                    <>
                        <div className='pt-4 grid grid-cols-6 items-center'>
                            <div className='col-span-2'>
                                <p>{t('common.approved')}</p>
                            </div>
                            <div className='col-span-4'>
                                <div className='grid grid-cols-2 gap-x-2'>
                                    <div className='col-span-1'>
                                        <label className="block text-sm">
                                            {t('common.from')}
                                        </label>
                                        <div className='mt-2'>
                                            <CustomDateTimePicker_01
                                                value={formData.requestedFrom}
                                                onChange={(newValue) => handleInputChange({
                                                    target: {
                                                        name: "requestedFrom",
                                                        value: newValue
                                                    }
                                                })}
                                                readOnly={true}
                                                formatDate={safeDateTimeFormat}
                                            />
                                        </div>
                                    </div>
                                    <div className='col-span-1'>
                                        <label className="block text-sm">
                                            {t('common.to')}
                                        </label>
                                        <div className='mt-2'>
                                            <CustomDateTimePicker_01
                                                value={formData.requestedTo}
                                                onChange={(newValue) => handleInputChange({
                                                    target: {
                                                        name: "requestedTo",
                                                        value: newValue
                                                    }
                                                })}
                                                readOnly={true}
                                                minDate={formData.requestedFrom}
                                                formatDate={safeDateTimeFormat}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div className='pt-5 grid grid-cols-12 md:grid-cols-6 items-center '>
                    <div className='col-span-12 md:col-span-2'>
                        <p>{t('common.leave-type')}</p>
                    </div>
                    <div className='col-span-12 md:col-span-4 mt-2 md:mt-0'>
                        <FormControl className='w-full'>
                            <CustomSelect
                                name="leaveType"
                                id='editLeaveType'
                                value={formData.leaveType}
                                onChange={handleInputChange}
                                options={leaveTypesData}
                                dataType="leaveType"
                                readOnly={true}
                            />
                        </FormControl>
                    </div>
                </div>
                {formData.leaveType !== 1 && (
                    <div className="pt-2 grid grid-cols-6 items-center">
                        <div className="col-span-6">
                            <CustomInputHorizontal
                                required={false}
                                title={
                                    <span className="inline-flex items-center gap-1">
                                        {t("common.used-leave")}
                                        {leaveEntitlementInfo.details?.length ? (
                                            <DefaultTooltip
                                                title={t("common.used-leave")}
                                                readMore={false}
                                                content={
                                                    <LeaveUsedDaysTooltipContent
                                                        details={leaveEntitlementInfo.details}
                                                        totalDays={leaveEntitlementInfo.usedDays}
                                                    />
                                                }
                                            >
                                                <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5 cursor-help" />
                                            </DefaultTooltip>
                                        ) : null}
                                    </span>
                                }
                                inputType="text"
                                name="leaveEntitlement"
                                value={leaveEntitlementInfo.text}
                                readOnly
                            />
                        </div>
                    </div>
                )}

                {formData.leaveType !== 1 && carryLeaveText && (
                    <div className="pt-2 grid grid-cols-6 items-center">
                        <div className="col-span-6">
                            <CustomInputHorizontal
                                required={false}
                                title={t("common.carry-leave", {
                                    year: new Date().getFullYear() - 1
                                })}
                                inputType="text"
                                value={carryLeaveText}
                                readOnly
                            />
                        </div>
                    </div>
                )}

                {isAdmin && formData.leaveType !== 1 && (
                    <div className='pt-5 grid grid-cols-6 items-center'>
                        <div className='col-span-6 md:col-span-2'>
                            <p>{t('common.leave-policy')}</p>
                        </div>
                        <div className='pt-2 md:pt-0 col-span-6 md:col-span-4'>
                            <FormControl className='w-full'>
                                <CustomSelect
                                    name="leavePolicy"
                                    id='editLeavePolicy'
                                    value={formData.leavePolicy}
                                    onChange={handleInputChange}
                                    options={policiesList}
                                    dataType="leavePolicy"
                                />
                            </FormControl>
                        </div>
                    </div>
                )}
                <div className='grid grid-cols-6 items-center mt-2'>
                    {formData.requestReason ? (
                        <Tooltip
                            title={<div>{formData.requestReason}</div>}
                            placement="top"
                            arrow
                        >
                            <div className='col-span-6'>
                                <CustomInputHorizontal
                                    title={t('common.leave-request')}
                                    inputType="text"
                                    name="requestReason"
                                    placeholder={t('common.leave-request')}
                                    handleInputChange={handleInputChange}
                                    value={formData.requestReason}
                                    readOnly
                                />
                            </div>
                        </Tooltip>
                    ) : (
                        <div className='col-span-6'>
                            <CustomInputHorizontal
                                title={t('common.leave-request')}
                                inputType="text"
                                name="requestReason"
                                placeholder={t('common.leave-request')}
                                handleInputChange={handleInputChange}
                                value={formData.requestReason}
                                readOnly
                            />
                        </div>
                    )}
                </div>

                <div className='grid grid-cols-6 items-center mt-2'>
                    <div className='col-span-6'>
                        <CustomInputHorizontal
                            title={t('common.created-time')}
                            inputType="text"
                            name="requestReason"
                            placeholder={t('common.created-time')}
                            handleInputChange={handleInputChange}
                            value={
                                formData.createdTime
                                    ? formatRequestDateTime(formData.createdTime)
                                    : ""
                            }
                            readOnly
                        />
                    </div>
                </div>

                <div className='grid grid-cols-6 items-center mt-2'>
                    <div className='col-span-6'>
                        <CustomInputHorizontal
                            title={
                                <>
                                    {t('common.approve-reject-reason')}
                                    <span className="text-[#EF4444] ml-1">*</span>
                                </>
                            }
                            inputType="text"
                            name="approveReason"
                            // isError={isEmailError}
                            // isSuccess={isEmailSuccess}
                            placeholder={t('common.approve-reject-reason')}
                            // message={validationMessages.typeName}
                            handleInputChange={handleInputChange}
                            value={formData.approveReason}
                        />
                        {/* <div className="flex justify-end mt-1 text-xs">
                            <span
                                className={
                                    formData.approveReason.length >= MAX_REASON_LENGTH
                                        ? "text-red-500"
                                        : "text-gray-400"
                                }
                            >
                                {formData.approveReason.length} / {MAX_REASON_LENGTH}
                            </span>
                        </div> */}
                    </div>

                </div>

                {isOverEntitlement && formData.leaveType !== 1 && (
                    <div className="pt-4">
                        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-md text-sm flex gap-2 items-center">
                            <WarningIcon className="inline mr-2 w-2 h-2" />
                            <div>{t("common.overEntitlementWarningAdmin")}</div>
                        </div>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <div className='flex gap-2 px-4 py-4'>
                    <ControlpanelErrorButton onClick={handleOpenRejectRequest} disabled={isLoading}>
                        <CloseIcon />
                        <p>{t('button.reject')}</p>
                    </ControlpanelErrorButton>
                    <ControlpanelSuccessButton onClick={(e) => {
                        handleOpenApproveRequest()
                    }} disabled={isLoading}>
                        <CheckIcon />
                        <p>{t('button.approve')}</p>
                    </ControlpanelSuccessButton>
                </div>
            </DialogActions>
            {openCorfirmApproveRequest && <CorfirmApproveRequestDialog onClose={handleCloseApproveRequest} formData={formData} onCloseDetail={handleCloseDialog} leaveInfo={leaveInfo} onRefresh={onRefresh} onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))} />}
            {openCorfirmRejectRequest && <CorfirmRejectRequestDialog onClose={handleCloseRejectRequest} formData={formData} onCloseDetail={handleCloseDialog} leaveInfo={leaveInfo} onRefresh={onRefresh} onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))} />}
        </CustomDialog>
    )
}
export default DetailRequestDialog